import { Component } from '@theme/component';
import { getFocusableElements } from '@theme/focus';
import { debounce, getDocumentScrollY, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const POINTER_OPEN_TYPES = new Set(['mouse', 'pen', 'touch']);

/** Stops programmatic/focus-induced scroll easing from css `scroll-behavior: smooth` during modal lifecycle. */
let htmlScrollBehaviorModalDepth = 0;
let htmlScrollBehaviorModalPrior = '';
let dialogHeaderColorDepth = 0;

function lockHtmlScrollBehaviorForModal() {
  if (htmlScrollBehaviorModalDepth === 0) {
    htmlScrollBehaviorModalPrior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
  }
  htmlScrollBehaviorModalDepth += 1;
}

function unlockHtmlScrollBehaviorForModal() {
  htmlScrollBehaviorModalDepth = Math.max(0, htmlScrollBehaviorModalDepth - 1);
  if (htmlScrollBehaviorModalDepth === 0) {
    document.documentElement.style.scrollBehavior = htmlScrollBehaviorModalPrior;
  }
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {AbortController | null} */
  #hostListenersAbort = null;

  /** @type {boolean} */
  #appliedHeaderColor = false;

  /**
   * @param {EventTarget | null} target
   */
  #targetOutsideDialog = (target) =>
    target instanceof Element &&
    this.contains(target) &&
    !this.refs.dialog.contains(target);

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    this.#hostListenersAbort = new AbortController();
    const { signal } = this.#hostListenersAbort;

    this.addEventListener('pointerdown', this.#handleHostPointerDown, { capture: true, signal });

    this.addEventListener('touchstart', this.#handleHostTouchStart, {
      capture: true,
      passive: true,
      signal,
    });

    this.addEventListener('keydown', this.#handleHostKeyDown, { capture: true, signal });
    this.addEventListener('click', this.#handleHostClick, { capture: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#hostListenersAbort?.abort();
    this.#hostListenersAbort = null;

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  /** @param {PointerEvent} event */
  #handleHostPointerDown = (event) => {
    if (!(event instanceof PointerEvent)) return;
    if (!POINTER_OPEN_TYPES.has(event.pointerType)) return;
    if (!this.#targetOutsideDialog(event.target)) return;

    this.dataset.dialogOpenedWithPointer = 'true';
  };

  /** @param {TouchEvent} event */
  #handleHostTouchStart = (event) => {
    if (!this.#targetOutsideDialog(event.target)) return;
    if (this.dataset.dialogOpenedWithPointer === 'false') return;

    this.dataset.dialogOpenedWithPointer = 'true';
  };

  /** @param {KeyboardEvent} event */
  #handleHostKeyDown = (event) => {
    const target = event.target;

    if (!(target instanceof Element) || !this.contains(target)) return;

    const { dialog } = this.refs;

    if (!dialog.contains(target) && (event.key === 'Enter' || event.key === ' ')) {
      this.dataset.dialogOpenedWithPointer = 'false';
    }

    if (!dialog.open || (event.key !== 'Escape' && event.key !== 'Tab')) return;

    this.dataset.dialogKeyboardInModal = 'true';
  };

  /** @param {MouseEvent} event */
  #handleHostClick = (event) => {
    if (!this.#targetOutsideDialog(event.target)) return;
    if (this.dataset.dialogOpenedWithPointer === 'false') return;

    this.dataset.dialogOpenedWithPointer = 'true';
  };

  #takeRestoreOutlineSuppress() {
    const opened = this.dataset.dialogOpenedWithPointer;
    const usedKeyboardInModal = this.dataset.dialogKeyboardInModal === 'true';

    delete this.dataset.dialogOpenedWithPointer;
    delete this.dataset.dialogKeyboardInModal;

    return opened === 'true' && !usedKeyboardInModal;
  }

  #focusFirstFocusableInDialog() {
    const { dialog } = this.refs;
    const focusable = getFocusableElements(dialog);
    const target = focusable[0];

    if (!target) return;

    const focus = () => {
      try {
        target.focus({ preventScroll: true, focusVisible: true });
      } catch {
        target.focus({ preventScroll: true });
      }
    };

    focus();
    queueMicrotask(focus);
    requestAnimationFrame(focus);
  }

  /** @param {'open' | 'close'} when */
  #suppressFocusedOutline(when) {
    const dialog = this.refs.dialog;

    /** @type {FocusOptions & { focusVisible?: boolean }} */
    const opts = { preventScroll: true, focusVisible: false };

    /** @type {HTMLElement | undefined} */
    let tracked;

    const detachTracked = () => {
      if (!tracked) return;
      delete tracked.dataset.dialogRestoreSuppressRing;
      tracked.removeEventListener('blur', onTrackedBlur);
      tracked = undefined;
    };

    const onTrackedBlur = () => detachTracked();

    /** @param {HTMLElement} el */
    const tag = (el) => {
      if (!(el instanceof HTMLElement)) return;

      detachTracked();
      tracked = el;
      tracked.dataset.dialogRestoreSuppressRing = 'true';
      tracked.addEventListener('blur', onTrackedBlur);

      try {
        tracked.focus(opts);
      } catch {
        tracked.focus({ preventScroll: true });
      }
    };

    const tryActive = () => {
      const el = document.activeElement;
      if (!(el instanceof HTMLElement)) return;

      if (when === 'open' && !dialog.contains(el)) return;

      tag(el);
    };

    tryActive();
    queueMicrotask(tryActive);
    requestAnimationFrame(tryActive);
    requestAnimationFrame(() => requestAnimationFrame(tryActive));

    if (when === 'open') {
      const stopListening = () => dialog.removeEventListener('focusin', onOpenFocusIn, true);

      /** @param {FocusEvent} event */
      const onOpenFocusIn = (event) => {
        const node = event.target;

        stopListening();

        if (!(node instanceof HTMLElement) || !dialog.contains(node)) return;

        tag(node);
      };

      dialog.addEventListener('focusin', onOpenFocusIn, true);

      requestAnimationFrame(() => {
        requestAnimationFrame(stopListening);
      });

      return;
    }

    const stopDoc = () => document.removeEventListener('focusin', onDocFocusIn, true);

    /** @param {FocusEvent} event */
    const onDocFocusIn = (event) => {
      const node = event.target;

      stopDoc();

      if (!(node instanceof HTMLElement)) return;

      tag(node);
    };

    document.addEventListener('focusin', onDocFocusIn, true);

    requestAnimationFrame(() => {
      requestAnimationFrame(stopDoc);
    });
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /** @type {number | null} */
  #searchModalWidthAtOpen = null;

  #handleSearchModalViewportResize = debounce(() => {
    if (this.id !== 'search-modal' || this.#searchModalWidthAtOpen === null) return;
    if (!this.refs.dialog.open) return;
    if (window.innerWidth === this.#searchModalWidthAtOpen) return;
    this.#searchModalWidthAtOpen = null;
    void this.closeDialog();
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog(event) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    if (this.dataset.dialogOpenedWithPointer !== 'false') {
      if (event instanceof KeyboardEvent) {
        this.dataset.dialogOpenedWithPointer = 'false';
      } else if (
        this.dataset.dialogOpenedWithPointer !== 'true' &&
        ((event instanceof PointerEvent && POINTER_OPEN_TYPES.has(event.pointerType)) ||
          event instanceof MouseEvent)
      ) {
        this.dataset.dialogOpenedWithPointer = 'true';
      }
    }

    this.dataset.dialogKeyboardInModal = '';

    this.#applyHeaderColor();

    if (this.id === 'search-modal') {
      if (document.documentElement.hasAttribute('data-search-modal-open-from-drawer')) {
        this.setAttribute('data-mobile-drawer-close', '');
        document.documentElement.removeAttribute('data-search-modal-open-from-drawer');
      } else {
        this.removeAttribute('data-mobile-drawer-close');
      }
    }

    const scrollY = getDocumentScrollY();
    this.#previousScrollY = scrollY;

    lockHtmlScrollBehaviorForModal();

    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;

    if (this.dataset.dialogOpenedWithPointer === 'false') {
      const closeBtn = dialog.querySelector('.close-button');

      if (closeBtn instanceof HTMLElement) {
        delete closeBtn.dataset.dialogRestoreSuppressRing;
      }
    } else if (this.dataset.dialogOpenedWithPointer === 'true') {
      const closeBtn = dialog.querySelector('.close-button');

      if (closeBtn instanceof HTMLElement) {
        closeBtn.dataset.dialogRestoreSuppressRing = 'true';
      }
    }

    dialog.showModal();

    if (this.dataset.dialogOpenedWithPointer === 'true') {
      this.#suppressFocusedOutline('open');
    } else if (this.dataset.dialogOpenedWithPointer === 'false') {
      this.#focusFirstFocusableInDialog();
    }

    this.dispatchEvent(new DialogOpenEvent());

    // Register click/keydown in the next frame so the opening click event
    // doesn't bubble into handleClick and immediately close the dialog.
    requestAnimationFrame(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      if (this.id === 'search-modal') {
        this.#searchModalWidthAtOpen = window.innerWidth;
        window.addEventListener('resize', this.#handleSearchModalViewportResize);
      }
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    if (this.id === 'search-modal') {
      window.removeEventListener('resize', this.#handleSearchModalViewportResize);
      this.#searchModalWidthAtOpen = null;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    try {
      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });

      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.overflowY = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

      const suppressRestoreOutline = this.#takeRestoreOutlineSuppress();

      dialog.close();
      dialog.classList.remove('dialog-closing');

      if (suppressRestoreOutline) {
        this.#suppressFocusedOutline('close');
      }

      if (this.id === 'search-modal') {
        this.removeAttribute('data-mobile-drawer-close');
      }

      const searchMenu = document.querySelector('search-menu');
      if (searchMenu && typeof searchMenu.reapplyBodyScrollLockIfActive === 'function') {
        searchMenu.reapplyBodyScrollLockIfActive();
      }

      this.dispatchEvent(new DialogCloseEvent());
    } finally {
      this.#restoreHeaderColor();
      unlockHtmlScrollBehaviorForModal();
    }
  };

  /**
   * Apply header drawer background color to header component via a CSS custom
   * property. The actual viewport gating is handled in CSS so it responds to
   * resizes automatically.
   */
  #applyHeaderColor() {
    const headerComponent = document.querySelector('header-component');
    const headerDrawer = this.querySelector('[data-header-drawer], [data-header-drawer-mobile]');

    if (!headerComponent || !headerDrawer) return;

    this.#appliedHeaderColor = true;

    const drawerStyle = getComputedStyle(headerDrawer);
    const drawerBg = drawerStyle.backgroundColor;

    if (headerComponent instanceof HTMLElement) {
      headerComponent.style.setProperty('--dialog-header-bg', drawerBg);

      const isMobileOnly = headerDrawer.hasAttribute('data-header-drawer-mobile');
      headerComponent.setAttribute(isMobileOnly ? 'data-dialog-header-mobile' : 'data-dialog-header', '');
    }

    dialogHeaderColorDepth += 1;
  }

  /**
   * Remove the dialog header color custom property and marker attributes.
   */
  #restoreHeaderColor() {
    if (!this.#appliedHeaderColor) return;

    const headerComponent = document.querySelector('header-component');

    if (!headerComponent) return;

    this.#appliedHeaderColor = false;
    dialogHeaderColorDepth = Math.max(0, dialogHeaderColorDepth - 1);

    if (dialogHeaderColorDepth === 0) {
      if (headerComponent instanceof HTMLElement) {
        headerComponent.style.removeProperty('--dialog-header-bg');
        headerComponent.removeAttribute('data-dialog-header');
        headerComponent.removeAttribute('data-dialog-header-mobile');
      }
    }
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /** @param {KeyboardEvent} event */
  handleTriggerActivateKeys = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.dataset.dialogOpenedWithPointer = 'false';
    this.showDialog(event);
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName, { bubbles: true, composed: true });
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName, { bubbles: true, composed: true });
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
